import React, { useReducer } from "react";
import { useDispatch } from "react-redux";
import { setMessageHandler } from "../../../reducers/selectedValues";
import { Box, Typography } from "@mui/material";
import { InputText } from "../../uiControls/inputText";
import { ComboBox } from "../../uiControls/comboBox";
import useHttpRequests from "../../../hooks/useHttpRequests";
import { MessageDisplay } from "../../common/messageDisplay";
import { SaveCancel } from "../../uiControls/saveCancel";
import { StatusControl } from "../../uiControls/statusControl";
import * as utils from "../../common/utils";

const initialFacilityIsValid = {
  name: "",
  supportEmail: "",
  functionalAreaKey: "",
  datasourceKey: "",
  timezoneKey: "",
  message: "",
  type: "",
};

export const FacilityUpdate = (props) => {
  const {
    setUpdateOpen,
    facilityAttributes,
    functionalAreas,
    datasources,
    timezones,
  } = props;
  const messageDispatch = useDispatch();
  const { sendRequest: updateFacility } = useHttpRequests();

  const [facilityDetails, setFacilityDetails] = useReducer(
    (state, updates) => ({ ...state, ...updates }),
    facilityAttributes
  );

  const [isValid, setIsValid] = useReducer(
    (state, updates) => ({ ...state, ...updates }),
    initialFacilityIsValid
  );

  const updateFacilityDetailsValue = (_key, value) => {
    setFacilityDetails({ [_key]: value });
    if (
      (value && _key !== "supportEmail") ||
      (value && _key === "supportEmail" && utils.validateEmail(value))
    ) {
      updateErrorMessage([_key], "", "");
    } else if (
      value &&
      _key === "supportEmail" &&
      !utils.validateEmail(value)
    ) {
      updateErrorMessage("supportEmail", "Email", "Enter ");
    }
  };

  const updateErrorMessage = (_key, header, errMsg) => {
    setIsValid({ [_key]: errMsg + header });
  };

  const onChangeHandler = (e, controlName) => {
    updateFacilityDetailsValue(
      controlName,
      e.target.type === "checkbox" ? e.target.checked : e.target.value
    );
  };

  const dropDownChangeHandler = (e, value, field) => {
    if (value?.id) {
      updateFacilityDetailsValue(field, value.id);
    } else {
      updateFacilityDetailsValue(field, 0);
    }
  };

  const cancelFacilityHandler = () => {
    messageDispatch(setMessageHandler({ type: null, message: null }));
    setUpdateOpen(false);
  };

  const validateFacilityDetails = () => {
    setIsValid(initialFacilityIsValid);

    var errorSummary = "";

    if (
      facilityDetails["supportEmail"] !== "" &&
      !utils.validateEmail(facilityDetails["supportEmail"])
    ) {
      updateErrorMessage("supportEmail", "Email", "Enter ");
      errorSummary += "Enter valid support email. \n";
    }

    if (
      !facilityDetails.functionalAreaKey ||
      facilityDetails.functionalAreaKey <= 0
    ) {
      updateErrorMessage("functionalAreaKey", "Functional Area", "Select ");
      errorSummary += "Select functional area. \n";
    }

    if (errorSummary !== "") {
      messageDispatch(
        setMessageHandler({ type: "error", message: errorSummary })
      );
    } else {
      const dataObj = {
        facilityKey: facilityDetails["facilityKey"],
        timezoneKey: facilityDetails["timezoneKey"],
        isTestFacility: facilityDetails["isTestFacility"] ?? false,
        supportEmail: facilityDetails["supportEmail"],
        functionalAreaKey: facilityDetails["functionalAreaKey"],
        datasourceKey: facilityDetails["datasourceKey"],
        active: facilityDetails["active"],
      };

      const getUpdateFacilityResponse = (resObj) => {
        if (
          !resObj ||
          !resObj["isSuccess"] ||
          (resObj["validationErrors"] !== undefined &&
            resObj["validationErrors"]?.length > 0)
        ) {
          messageDispatch(
            setMessageHandler({
              type: "error",
              message:
                (resObj["message"] ? resObj["message"] : "") +
                (resObj["validationErrors"]?.length > 0
                  ? "\n" + resObj["validationErrors"]?.join("\n")
                  : ""),
            })
          );
          return;
        }
        messageDispatch(
          setMessageHandler({
            type: "success",
            message: "Facility updated successfully!",
          })
        );
        setTimeout(() => {
          cancelFacilityHandler();
        }, 2000);
      };

      updateFacility(
        {
          url: "Facilities/" + dataObj["facilityKey"],
          method: "PUT",
          body: dataObj,
        },
        getUpdateFacilityResponse
      );
    }
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          width: "100%",
          overflow: "hidden",
          padding: "5px",
        }}
      >
        <div
          style={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            gap: "20px",
          }}
        >
          <MessageDisplay />
          <Typography>
            <label>Name:</label> {facilityDetails.name}
          </Typography>

          <InputText
            type="email"
            label="Support Email"
            id={"facilityDetails.supportEmail"}
            isError={isValid["supportEmail"] !== ""}
            value={facilityDetails["supportEmail"]}
            onChange={(e) => onChangeHandler(e, "supportEmail")}
            isRequired={false}
          />

          <ComboBox
            id={"facilityDetails.functionalAreaKey"}
            label="Functional Area"
            isError={isValid["functionalAreaKey"] !== ""}
            isRequired={true}
            isDisabled={false}
            selectedValue={facilityDetails["functionalAreaTitle"]}
            list={functionalAreas}
            onChange={(e, value) =>
              dropDownChangeHandler(e, value, "functionalAreaKey")
            }
          />

          <ComboBox
            id={"facilityDetails.datasourceKey"}
            label="Data Source"
            isError={isValid["datasourceKey"] !== ""}
            isRequired={true}
            isDisabled={false}
            selectedValue={facilityDetails["datasourceTitle"]}
            list={datasources}
            onChange={(e, value) =>
              dropDownChangeHandler(e, value, "datasourceKey")
            }
          />

          <ComboBox
            id={"facilityDetails.timezoneKey"}
            label="Time Zone"
            isError={isValid["timezoneKey"] !== ""}
            isRequired={true}
            isDisabled={false}
            selectedValue={facilityDetails["timezoneTitle"]}
            list={timezones}
            onChange={(e, value) =>
              dropDownChangeHandler(e, value, "timezoneKey")
            }
          />

          <StatusControl
            label="Test Facility"
            isRequired={false}
            fieldName={"isTestFacility"}
            active={facilityDetails.isTestFacility}
            updateStatusValue={updateFacilityDetailsValue}
          />

          <StatusControl
            label="Active"
            isRequired={false}
            fieldName={"active"}
            active={facilityDetails.active}
            updateStatusValue={updateFacilityDetailsValue}
          />
          <SaveCancel
            cancelCall={() => cancelFacilityHandler()}
            saveCall={() => validateFacilityDetails()}
          />
        </div>
      </Box>
    </>
  );
};
